<template>
    <div class="privacy">
        <span class="privacy__data-label">Effective date</span><span class="privacy__data">{{ data.policyDate }}</span>
        <template v-if="data.licensee.policies.privacy">
            <h3>Who's who</h3>
            <p>
                Welcome to <strong>Parity Projects</strong>’ Plan Builder! The Plan Builder is a tool we’ve created for low carbon home services
                around the UK to offer to their clients.
            </p>
            <template v-if="data.licensee.isSubsidiary">
                <p>
                    This Plan Builder hosted at <a :href="data.app.url" target="_blank">{{ data.app.url }}</a> is designed and owned by
                    <strong>Parity Projects Ltd</strong> who runs <strong>{{ data.app.name }}</strong>. This privacy notice explains how the data
                    shown and collected on the Plan Builder is processed.
                </p>
                <p>
                    The <strong>{{ data.app.name }}</strong> Privacy Notice can be found <a :href="data.licensee.policies.privacy" target="_blank">here</a>.
                </p>
            </template>
            <template v-else>
                <p>
                    This Plan Builder hosted at <a :href="data.app.url" target="_blank">{{ data.app.url }}</a> is designed and owned by
                    <strong>Parity Projects Ltd</strong> and is licensed to <strong>{{ data.licensee.name }}</strong> who runs
                    <strong>{{ data.app.name }}</strong>, whose privacy notice can be found
                    <a :href="data.licensee.policies.privacy" target="_blank">here</a>.
                </p>
                <p>
                    This privacy notice explains how <strong>Parity Projects Ltd</strong> processes the data shown and collected on the Plan Builder.
                </p>
            </template>
        </template>
        <h3>Our contact details</h3>
        <p>
            <strong>Parity Projects</strong>’ postal, and registered business, address is Fore 2, 2 Huskisson Way, Shirley, Solihull, West Midlands, England, B90 4SS. (<a href="https://www.parityprojects.com" target="_blank">www.parityprojects.com</a>)
        </p>
        <p>
            If you have any questions please contact us at <a href="mailto:info@parityprojects.com?Subject=Contact from Plan Builder">info@parityprojects.com</a>
            and we will be happy to help.
        </p>
        <h3>The type of information we have</h3>
        <p>
            Personal data including your contact details, and details needed in order to be able provide the services you request. In the next
            section we show what this is and what we do with this.
        </p>
        <p>
            We also hold information about homes in the UK. When details about your property includes your address we treat this as
            personal data so we have also included this below.
        </p>
        <h3>How and why we have this, and what we do with it</h3>
        <p>
            We want you to understand exactly how we use your data. Please get in touch if you have any questions, if you want to withdraw
            consent for a use, or you don’t want us to get in touch again. Consent for marketing can be managed directly through
            <router-link :to="{ name: 'MyAccount' }">
                your account
            </router-link>.
        </p>
        <ul class="privacy__gdpr-items">
            <li>
                <gdpr name="Contact details"
                      what="Name, address, email, telephone number, your availability"
                      how="We get these details from you if you enter them into the Plan Builder."
                      basis="Legitimate interests">
                    <p>
                        We collect, hold, and share this information with our partner organisations (see "Sharing your information" below)
                        so that they can get in touch with you and provide the service you request. You can find more information about this in the
                        <a :href="data.licensee.policies.privacy" target="_blank">{{ data.app.name }} privacy notice</a>.
                    </p>
                </gdpr>
            </li>
            <li>
                <gdpr name="Your consents"
                      how="We get these details from you if you enter them into the Plan Builder."
                      basis="Consent">
                    <p>
                        We ask you whether you would like to give your consent for us to use your data in specific ways. These
                        purposes are clearly explained where you have the option to give your consent.
                    </p>
                    <p>
                        We note that you have given your consent and provide the consent to our relevant partner organisations.
                        You can withdraw your consent at any time in
                        <router-link :to="{ name: 'MyAccount' }">
                            your account
                        </router-link>.
                    </p>
                </gdpr>
            </li>
            <li>
                <gdpr name="UK homes"
                      :show-how="true"
                      what="Information about homes in the UK"
                      basis="Legitimate interests">
                    <!--suppress HtmlDeprecatedAttribute -->
                    <template slot="how">
                        <p>
                            We get this information from Energy Performance Certificate (EPC) Data and Ordnance Survey Data.
                            You can read more about EPC Data
                            <a href="https://www.gov.uk/government/publications/making-better-use-of-energy-performance-of-buildings-data-privacy-impact-assessment" target="_blank">here</a>.
                        </p>
                        <p>
                            We also try to improve this information by using data from nearby houses and visual surveys.
                        </p>
                    </template>
                    <p>
                        This lets us make recommendations to you about how to make your home lower carbon.
                    </p>
                    <p class="gdpr__note">
                        Where property data includes an address we treat it as personal data.
                    </p>
                </gdpr>
            </li>
            <li>
                <gdpr name="Your home"
                      what="Information that you enter about a property."
                      how="You update 'home details' online."
                      basis="Legitimate interests">
                    <p>
                        When you update details about a property in the Plan Builder this is recorded within the account that you have set up so
                        that you can generate better recommendations. They cannot be seen by someone else who looks up the address.
                    </p>
                </gdpr>
            </li>
            <li>
                <gdpr :name="`${data.app.name} account details`"
                      what="Account details, your motivation for renovating, improvements selections and information about your home and plans."
                      how="Choices you make / information entered when using the Plan Builder."
                      basis="Legitimate interests">
                    <p>
                        We use this information to provide and maintain your account and save preferences and plans so that you can return later.
                        We may contact you in order to know whether the account is still needed, or in order to update or verify details.
                    </p>
                    <p>
                        If you 'submit' a plan we, or our partners, use your preferences and selections to understand what works might interest you.
                    </p>
                    <p>
                        If you choose to submit a plan to a partner organisation then <strong>Parity Projects Ltd.</strong> is no longer the Data Controller.
                        How these partner organisations use your data will be covered by the privacy notices on their websites.
                    </p>
                </gdpr>
            </li>
            <li>
                <gdpr name="Site usage"
                      :show-how="true"
                      what="Your IP address and how you find and use our site"
                      why="So that we can improve the Plan Builder."
                      basis="Legitimate interests">
                    <p slot="how">
                        We get this information through cookies/tags placed on our site. For more information, see our
                        <a @click="onCookiesPolicyClick">Cookies & Local data policy</a>.
                    </p>
                </gdpr>
            </li>
        </ul>
        <h3>Third Party Services</h3>
        <p>The Plan Builder uses the following third party technical services that make use of your data:</p>
        <ul>
            <li><strong>Microsoft Azure Web Hosting</strong> - <a href="https://privacy.microsoft.com/en-us/privacystatement#mainenterprisedeveloperproductsmodule" target="_blank">Privacy Statement</a></li>
            <li><strong>Google Analytics</strong> - <a href="https://www.google.com/policies/privacy/" target="_blank">Privacy Policy</a></li>
        </ul>
        <h3>Sharing your information</h3>
        <template v-if="loaded">
            <p>
                The ways we share your information are shown above. So that you can see clearly who we share your data with the organisations
                are listed here:
            </p>
            <ul v-if="isUmbrella"
                class="privacy__partners-list">
                <li v-for="item in umbrellaPartners"
                    class="privacy__partners-list__item"
                    :key="item.name">
                    {{ item.name }}<span v-if="item.schemes" class="privacy__partners-list__item__schemes">&nbsp;({{ item.schemes }})</span>
                </li>
            </ul>
            <ul v-else
                class="privacy__partners-list">
                <li v-if="showRetrofitWorksAsWell"
                    class="privacy__partners-list__item">
                    <strong>RetrofitWorks Ltd</strong>
                </li>
                <li class="privacy__partners-list__item">
                    <strong>{{ data.licensee.name }}</strong>
                </li>
            </ul>
        </template>
        <template v-else>
            <p><strong>Parity Projects Ltd</strong> partner organisations are unavailable</p>
        </template>
        <h3>How we store your information</h3>
        <p>
            Your information is held securely in our systems, which are located on our premises or those of carefully selected third
            parties. Electronically stored data is in secure cloud-based systems, which are located in the European Economic Area, or,
            if outside the EEA, are covered by the EU-US Privacy Shield framework.
        </p>
        <p>
            We keep your personal information for long enough to carry out the task it was collected for. The amount of time varies
            depending on the type of data and how we use it.
        </p>
        <h3>Your data protection rights</h3>
        <p>
            You have the following rights under data protection law, and <a href="https://ico.org.uk/for-organisations/guide-to-data-protection/guide-to-the-general-data-protection-regulation-gdpr/individual-rights/" target="_blank">information explaining these rights can be found on the Information Commissioner's Office (ICO) website</a>.
        </p>
        <right title="Your right of access"
               description="You have the right to ask us for copies of your personal information" />
        <right title="You can withdraw consent"
               description="You can withdraw consent at any time for any of the processing that relies on Consent" />
        <right title="Your right to object to processing"
               description="You have the right to object to the processing of your personal data in certain circumstances. You always have the right to object to processing for the purposes of direct marketing" />
        <right title="Your right to erasure"
               description="You have the right to ask us to erase your personal information in certain circumstances" />
        <right title="Your right to restriction of processing"
               description="You have the right to ask us to restrict the processing of your information in certain circumstances" />
        <right title="Your right to rectification"
               description="You have the right to ask us to rectify or complete information you think is inaccurate, and we must consider whether this request is founded and not excessive" />
        <right title="Your right to data portability"
               description="You have the right to ask that we transfer the information you gave us to another organisation, or to you, in certain circumstances" />
        <p>
            You do not have to pay to exercise your rights. If you make a request, we have one month to respond to you.
        </p>
        <p>
            Please contact us at <a href="mailto:info@parityprojects.com?Subject=Data Protection Request">info@parityprojects.com</a> if you wish to make a request.
        </p>
        <h3>How to complain</h3>
        <p>
            You can complain to us by emailing us at <a href="mailto:info@parityprojects.com?Subject=Data Protection Request">info@parityprojects.com</a>.
        </p>
        <p>
            You can complain to the ICO if you are unhappy with how we have used your data.
        </p>
        <p>
            Our ICO registration number is Z325526X
        </p>
        <p>
            The ICO’s address:
        </p>
        <p>
            Information Commissioner’s Office<br>
            Wycliffe House<br>
            Water Lane<br>
            Wilmslow<br>
            Cheshire<br>
            SK9 5AF<br>
            Helpline number: 0303 123 1113
        </p>
    </div>
</template>

<script>

import PolicyPresenter from './policy-presenter'
import CookiesPolicy from './cookies-policy'

import Privacy from './privacy'

export default {

    mixins: [
        PolicyPresenter,
    ],

    /**
     * @returns {string}
     */
    get directRouteName() {
        return 'PrivacyPolicyViewer'
    },

    async mounted() {
        if (!this.loaded) {
            this.umbrellaPartners = await this.getUmbrellaPartners()
            this.loaded = true
        }
    },

    methods: {

        onCookiesPolicyClick() {
            this.showPolicy(CookiesPolicy, 'Cookies & Local Data Policy')
        },
    },

    components: {
        Gdpr: Privacy.GdprDataItem,
        Right: Privacy.GdprRight,
    },
}

</script>

<style lang="scss">

.privacy {
    @include policy;

    @include element('gdpr-items') {
      @include unstyle-list;
      padding-top: 0.5rem;
  }
}

</style>

